import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Card, CardBody, Col, Row, Spinner, Table } from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import SysAdminService from "../../services/sysadmin";

function SysAdminDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [deletedCompanies, setDeletedCompanies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const statsRes = await SysAdminService.getCompaniesStats();
      if (statsRes.data?.status === 200) {
        setCompanies(statsRes.data.data || []);
      } else {
        toast.error(statsRes.data?.message || "Error loading stats");
      }
    } catch (err) {
      toast.error("Error loading companies stats");
    }
    try {
      const deletedRes = await SysAdminService.getDeletedCompanies();
      if (deletedRes.data?.status === 200) {
        setDeletedCompanies(deletedRes.data.data || []);
      }
    } catch (err) {
      // silently ignore - deleted companies is optional
    }
    setIsLoading(false);
  };

  const handleRestore = async (companyId) => {
    try {
      const res = await SysAdminService.restoreCompany({ companyId });
      if (res.data?.status === 200) {
        toast.success(t("SYSADMIN.RESTORE_SUCCESS") || "Company restored");
        loadStats();
      } else {
        toast.error(res.data?.message || t("SYSADMIN.RESTORE_ERROR"));
      }
    } catch (err) {
      toast.error(t("SYSADMIN.RESTORE_ERROR") || "Error restoring company");
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner color="primary" />
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-title-box d-flex align-items-center justify-content-between">
        <h4 className="mb-0">{t("SYSADMIN.DASHBOARD") || "לוח מחוונים"}</h4>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="mini-stat bg-primary text-white">
            <CardBody>
              <div className="text-center">
                <h5 className="text-white">{companies.length}</h5>
                <p className="mb-0">{t("SYSADMIN.TOTAL_COMPANIES") || "סה\"כ חברות"}</p>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mini-stat bg-success text-white">
            <CardBody>
              <div className="text-center">
                <h5 className="text-white">
                  {companies.reduce((sum, c) => sum + c.terminalCount, 0)}
                </h5>
                <p className="mb-0">{t("SYSADMIN.TOTAL_TERMINALS") || "סה\"כ מסופים"}</p>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mini-stat bg-info text-white">
            <CardBody>
              <div className="text-center">
                <h5 className="text-white">
                  {companies.reduce((sum, c) => sum + c.agentCount, 0)}
                </h5>
                <p className="mb-0">{t("SYSADMIN.TOTAL_AGENTS") || "סה\"כ סוכנים"}</p>
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="mini-stat bg-warning text-dark">
            <CardBody>
              <div className="text-center">
                <h5>{companies.reduce((sum, c) => sum + c.employeeCount, 0)}</h5>
                <p className="mb-0">{t("SYSADMIN.TOTAL_EMPLOYEES") || "סה\"כ עובדים"}</p>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Companies Grid */}
      <Row>
        {companies.map((company) => (
          <Col lg={4} md={6} key={company.id}>
            <Card className="card-box">
              <CardBody>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h5 className="mb-1">{company.companyName}</h5>
                    {company.phone && (
                      <small className="text-muted">
                        <i className="ri-phone-line me-1"></i>
                        {company.phone}
                      </small>
                    )}
                  </div>
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate(`/sysadmin/company/${company.id}`)}
                  >
                    <i className="ri-settings-3-line me-1"></i>
                    {t("SYSADMIN.MANAGE") || "ניהול"}
                  </button>
                </div>

                <Row className="text-center">
                  <Col xs={4}>
                    <div className="p-2 border rounded">
                      <h6 className="mb-0 text-primary">{company.terminalCount}</h6>
                      <small className="text-muted">{t("SYSADMIN.TERMINALS") || "מסופים"}</small>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="p-2 border rounded">
                      <h6 className="mb-0 text-info">{company.agentCount}</h6>
                      <small className="text-muted">{t("SYSADMIN.AGENTS") || "סוכנים"}</small>
                    </div>
                  </Col>
                  <Col xs={4}>
                    <div className="p-2 border rounded">
                      <h6 className="mb-0 text-warning">{company.employeeCount}</h6>
                      <small className="text-muted">{t("SYSADMIN.EMPLOYEES") || "עובדים"}</small>
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Deleted Companies Section */}
      {deletedCompanies.length > 0 && (
        <div className="mt-4">
          <div className="d-flex align-items-center mb-3">
            <Button
              color="outline-secondary"
              size="sm"
              onClick={() => setShowDeleted(!showDeleted)}
            >
              <i className={`ri-arrow-${showDeleted ? "up" : "down"}-s-line me-1`}></i>
              {t("SYSADMIN.DELETED_COMPANIES") || "חברות מחוקות"} ({deletedCompanies.length})
            </Button>
          </div>

          {showDeleted && (
            <Card>
              <CardBody>
                <Table striped bordered responsive size="sm">
                  <thead className="table-light">
                    <tr>
                      <th>#</th>
                      <th>{t("SYSADMIN.COMPANY_NAME") || "שם חברה"}</th>
                      <th>{t("SYSADMIN.COMPANY_PHONE") || "טלפון"}</th>
                      <th style={{ width: "120px" }} className="text-center">
                        {t("SYSADMIN.ACTIONS") || "פעולות"}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {deletedCompanies.map((company) => (
                      <tr key={company.id}>
                        <td>{company.id}</td>
                        <td>{company.companyName}</td>
                        <td>{company.phone || "-"}</td>
                        <td className="text-center">
                          <Button
                            color="success"
                            size="sm"
                            onClick={() => handleRestore(company.id)}
                          >
                            <i className="ri-refresh-line me-1"></i>
                            {t("SYSADMIN.RESTORE") || "שחזר"}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          )}
        </div>
      )}
    </React.Fragment>
  );
}

export default SysAdminDashboard;
